// src/components/charts/bepaling-usage/bepaling-usage-chart.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, Spinner, Alert } from 'react-bootstrap';
import apiService from '@/api-service';
import { BestekFilterParams, mapFilterParamsToQueryParams } from '@/components/bestek-filter/bestek-filter-params';

interface BepalingUsageChartProps {
    bepalingID: number;
    bestekFilterParams: BestekFilterParams;
    lidNummer?: string; // FIXED: Add optional lid nummer prop for filtering specific lid
}

interface ChartDataPoint {
    month: string;
    vermeldingen: number;
    bestekken: number;
}

interface UsageTotals {
    vermeldingen: number;
    bestekken: number;
    periode: string;
}

interface BepalingInfo {
    nummer: string;
    titel: string;
    hoofdstuk_titel: string;
    paragraaf_titel: string;
    lid_nummer?: string;
}

const BepalingUsageChart: React.FC<BepalingUsageChartProps> = ({ 
    bepalingID, 
    bestekFilterParams,
    lidNummer // FIXED: Accept lid nummer prop
}) => {
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
    const [totals, setTotals] = useState<UsageTotals>({ vermeldingen: 0, bestekken: 0, periode: '' });
    const [bepalingInfo, setBepalingInfo] = useState<BepalingInfo>({ 
        nummer: '', 
        titel: '', 
        hoofdstuk_titel: '', 
        paragraaf_titel: '',
        lid_nummer: ''
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    // FIXED: Updated fetch function with lid nummer support
    const fetchUsageData = useCallback(async () => {
        if (!bepalingID) return;
        
        try {
            setLoading(true);
            setError('');
            
            console.log('Fetching usage chart data for:', {
                bepaling_id: bepalingID,
                lid_nummer: lidNummer,
                bestek_filter: mapFilterParamsToQueryParams(bestekFilterParams)
            });
            
            const response = await apiService.post('/bepaling/usage-chart/', {
                bepaling_id: bepalingID,
                bestek_filter: mapFilterParamsToQueryParams(bestekFilterParams),
                lid_nummer: lidNummer // FIXED: Pass lid nummer to API
            });
            
            console.log('Usage chart response:', response.data);
            
            setChartData(response.data.chart_data || []);
            setTotals(response.data.totals || { vermeldingen: 0, bestekken: 0, periode: '' });
            setBepalingInfo(response.data.bepaling_info || { 
                nummer: '', 
                titel: '', 
                hoofdstuk_titel: '', 
                paragraaf_titel: '',
                lid_nummer: ''
            });
            
        } catch (error: any) {
            console.error('Error fetching usage chart data:', error);
            setError(error.response?.data?.error || 'Er is een fout opgetreden bij het laden van de chart data');
            setChartData([]);
            setTotals({ vermeldingen: 0, bestekken: 0, periode: '' });
        } finally {
            setLoading(false);
        }
    }, [bepalingID, bestekFilterParams, lidNummer]); // FIXED: Add lidNummer to dependencies

    useEffect(() => {
        fetchUsageData();
    }, [fetchUsageData]);

    // Custom tooltip for the chart
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white border border-gray-300 rounded p-2 shadow">
                    <p className="font-medium">{`Maand: ${label}`}</p>
                    <p className="text-blue-600">{`Vermeldingen: ${payload[0]?.value || 0}`}</p>
                    <p className="text-green-600">{`Bestekken: ${payload[1]?.value || 0}`}</p>
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Card className="mt-3">
                <Card.Body className="text-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Gebruik data laden...
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="mt-3">
                <Card.Body>
                    <Alert variant="danger">
                        <strong>Fout bij laden chart data:</strong> {error}
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="mt-3">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h6 className="mb-1">
                            Gebruik van bepaling {bepalingInfo.nummer}
                            {/* FIXED: Show lid specific info if filtering on lid */}
                            {lidNummer && ` (lid ${lidNummer})`}
                        </h6>
                        <small className="text-muted">
                            {bepalingInfo.hoofdstuk_titel} - {bepalingInfo.paragraaf_titel}
                            {bepalingInfo.titel && ` - ${bepalingInfo.titel}`}
                        </small>
                    </div>
                    <div className="text-end">
                        <div className="small">
                            <strong>{totals.vermeldingen}</strong> vermeldingen in <strong>{totals.bestekken}</strong> bestekken
                        </div>
                        <div className="small text-muted">{totals.periode}</div>
                    </div>
                </div>
            </Card.Header>
            <Card.Body>
                {chartData.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-muted">
                            Geen gebruik data gevonden voor de geselecteerde filters.
                            {lidNummer && ` (gefilterd op lid ${lidNummer})`}
                        </p>
                    </div>
                ) : (
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer>
                            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="month" 
                                    tick={{ fontSize: 12 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Line 
                                    type="monotone" 
                                    dataKey="vermeldingen" 
                                    stroke="#0066cc" 
                                    strokeWidth={2}
                                    dot={{ fill: '#0066cc', strokeWidth: 2, r: 4 }}
                                    name="Vermeldingen"
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="bestekken" 
                                    stroke="#28a745" 
                                    strokeWidth={2}
                                    dot={{ fill: '#28a745', strokeWidth: 2, r: 4 }}
                                    name="Bestekken"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default BepalingUsageChart;